import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import BookCard from "@/components/BookCard";
import { Download, BookOpen } from "lucide-react";
import { toast } from "sonner";

export function Library() {
  const { user } = useAuth();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/library").then((r) => setBooks(r.data)).finally(() => setLoading(false));
  }, []);

  const download = async (id) => {
    try {
      const { data } = await api.get(`/books/${id}/download`);
      window.open(data.url, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Téléchargement indisponible");
    }
  };

  const readOnline = async (id) => {
    try {
      const { data } = await api.get(`/books/${id}/read`);
      window.open(data.url, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lecture en ligne indisponible");
    }
  };

  if (!user) return <div className="max-w-3xl mx-auto px-6 py-24 text-center"><p>Connectez-vous pour accéder à votre bibliothèque.</p><Link to="/login"><Button className="mt-6 rounded-sm">Se connecter</Button></Link></div>;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 fade-in">
      <div className="label-eyebrow text-accent mb-3">Ma bibliothèque</div>
      <h1 className="font-serif text-5xl tracking-tight">Vos livres</h1>
      <p className="mt-2 text-muted-foreground">Tous vos ouvrages achetés, disponibles en téléchargement.</p>

      {loading ? <div className="mt-16 text-muted-foreground">Chargement...</div> : books.length === 0 ? (
        <div className="mt-16 text-center py-16 border border-dashed border-border">
          <BookOpen className="w-8 h-8 text-muted-foreground mx-auto" />
          <p className="mt-4 text-muted-foreground">Aucun livre dans votre bibliothèque.</p>
          <Link to="/catalogue"><Button className="mt-6 rounded-sm">Explorer le catalogue</Button></Link>
        </div>
      ) : (
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {books.map((b) => (
            <div key={b.id} className="border border-border p-4 flex gap-4">
              <img src={b.cover_url} alt={b.title} className="w-24 h-36 object-cover" />
              <div className="flex-1 flex flex-col">
                <div className="label-eyebrow text-muted-foreground">{b.category}</div>
                <h3 className="font-serif text-lg leading-tight mt-1">{b.title}</h3>
                <div className="text-xs text-muted-foreground mt-1">{b.author}</div>
                <div className="mt-auto pt-4 space-y-2">
                  {b.flipbook_url && (
                    <Button data-testid={`library-read-${b.id}`} onClick={() => readOnline(b.id)} size="sm" className="rounded-sm w-full">
                      <BookOpen className="w-4 h-4 mr-2" /> Lire en ligne
                    </Button>
                  )}
                  <Button data-testid={`library-download-${b.id}`} onClick={() => download(b.id)} size="sm" variant="outline" className="rounded-sm w-full">
                    <Download className="w-4 h-4 mr-2" /> Télécharger le PDF
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Wishlist() {
  const [books, setBooks] = useState([]);
  useEffect(() => { api.get("/wishlist").then((r) => setBooks(r.data)); }, []);
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 fade-in">
      <div className="label-eyebrow text-accent mb-3">Favoris</div>
      <h1 className="font-serif text-5xl tracking-tight">Ma liste de souhaits</h1>
      {books.length === 0 ? (
        <p className="mt-12 text-muted-foreground">Votre liste est vide.</p>
      ) : (
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {books.map((b) => <BookCard key={b.id} book={b} />)}
        </div>
      )}
    </div>
  );
}

export function Orders() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/orders").then((r) => setOrders(r.data)); }, []);
  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 fade-in">
      <div className="label-eyebrow text-accent mb-3">Historique</div>
      <h1 className="font-serif text-5xl tracking-tight">Mes commandes</h1>
      {orders.length === 0 ? (
        <p className="mt-12 text-muted-foreground">Aucune commande pour l'instant.</p>
      ) : (
        <div className="mt-12 space-y-4">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.id}`} className="border border-border p-6 flex justify-between items-center">
              <div>
                <div className="text-xs text-muted-foreground">Commande #{o.id.slice(-8)}</div>
                <div className="text-sm mt-1">{new Date(o.created_at).toLocaleDateString("fr-FR")}</div>
                <div className="text-xs text-muted-foreground mt-1">{o.book_ids?.length || 0} livre(s)</div>
              </div>
              <div className="text-right">
                <div className="font-serif text-xl">{(o.amount || 0).toFixed(2).replace(".", ",")} €</div>
                <div className="label-eyebrow text-accent mt-1">{o.status}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
